// Unread markers (migration 081): opening a ticket marks it seen, "Mark as unread"
// puts the bold back, and My Day asks the server which of its tickets are unread.
//
// ticket_reads is a personal table (like ticket_watchers), so the admin UI writes it
// directly; no ticket row is touched (CLAUDE.md §6).

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './auth-guard';
import { chunk } from './page-all';
import { supabase } from './supabase';
import { patchTicketFields } from './ticket-actions';
import { invalidateTicketLists } from './queries';
import { toastFailure, toastSuccess } from './toast';

export function useTicketReads() {
  const qc = useQueryClient();
  const { teamMember } = useAuth();
  const me = teamMember?.slack_user_id ?? null;

  const write = useCallback(
    async (ticketId: string, unread: boolean) => {
      if (!me) return;
      // The row (and the badge counts) change at once; the server answers after.
      patchTicketFields(qc, ticketId, { unread });
      const { error } = await supabase.from('ticket_reads').upsert(
        { slack_user_id: me, ticket_id: ticketId, seen_at: new Date().toISOString(), marked_unread: unread },
        { onConflict: 'slack_user_id,ticket_id' },
      );
      if (error) {
        invalidateTicketLists(qc); // put the lists back to what the server says
        throw error;
      }
      // The Dashboard's Unread count is the server's: refetch it (only a mounted list
      // fetches now; the rest are just marked stale until you are back on them).
      qc.invalidateQueries({ queryKey: ['ticket-search-v2'] });
      qc.invalidateQueries({ queryKey: ['ticket-unread-flags'] });
    },
    [qc, me],
  );

  /** Opening a ticket. Best effort: a failure just leaves it bold. */
  const markSeen = useCallback((ticketId: string) => void write(ticketId, false).catch(() => {}), [write]);

  /** "Mark as unread". */
  const markUnread = useCallback(
    async (ticketId: string) => {
      try {
        await write(ticketId, true);
        toastSuccess('Marked as unread', 'It will show in bold until you open it again.');
      } catch (e) {
        toastFailure(e, "Couldn't mark that as unread");
      }
    },
    [write],
  );

  return { markSeen, markUnread };
}

/** Which of these tickets are unread for me (My Day, which does not use the search). */
export function useTicketUnreadFlags(ids: string[]) {
  return useQuery({
    queryKey: ['ticket-unread-flags', ids],
    enabled: ids.length > 0,
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<Map<string, boolean>> => {
      const out = new Map<string, boolean>();
      for (const part of chunk(ids, 400)) {
        const { data, error } = await supabase.rpc('ticket_unread_flags', { p_ids: part });
        if (error) throw error;
        for (const r of data ?? []) out.set(r.ticket_id, r.unread);
      }
      return out;
    },
  });
}
